<script lang="ts" setup>
  import Box from '@leaflink/stash/Box.vue';
  import Button from '@leaflink/stash/Button.vue';
  import IconLabel from '@leaflink/stash/IconLabel.vue';
  import Illustration from '@leaflink/stash/Illustration.vue';
  import useModals from '@leaflink/stash/useModals.vue';

  import DirectPayTermsConfirmation from '@/components/TermsAndConditions/components/DirectPayTermsConfirmation.vue';
  import LinkABankCta from '@/components/TermsAndConditions/components/LinkABankCta.vue';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';

  const modal = useModals();
  const userStore = useUserStore();

  function openTerms() {
    modal.open({
      component: DirectPayTermsConfirmation,
      attributes: {
        isOpen: true,
        onAccepted: () => {
          modal.close();
          openBankLinking();
        },
        onDismiss: () => {
          modal.close();
        },
      },
    });
  }

  function openBankLinking() {
    modal.open({
      component: LinkABankCta,
      attributes: {
        isOpen: true,
        onDismiss: () => modal.close(),
      },
    });
  }
</script>

<template>
  <div class="bg-ice-100" :class="$style.wrapper" data-test="join-today|seller-direct">
    <header class="bg-purple" :class="$style.header">
      <div class="container text-center align-center" :class="[$style.hero, $style['container--narrow']]">
        <img
          class="inline-block"
          :src="'/static/img/branding/logoFullPaymentsWhite.svg'"
          :alt="$t('common.llPayments')"
          :class="$style['payments-logo']"
        />
        <h1 class="ll-mt-4 ll-mb-4 text-white font-weight-bold" :class="$style['hero-heading']">
          {{ i18n.t('sellerJoinTodayDirect.pageHeader') }}
        </h1>
        <h2 class="text-white" :class="$style['hero-description']">
          {{ i18n.t('sellerJoinTodayDirect.pageSubheader') }}
        </h2>
      </div>
    </header>

    <div class="container" :class="$style.content">
      <div class="lg:grid lg:grid-cols-2 lg:gap-12">
        <Box class="ll-pa-3 md:p-12">
          <div class="flex text-ice-700">
            <Illustration class="ll-mr-2 flex-none" name="approved" />
            <div>
              <h3>{{ i18n.t('sellerJoinTodayDirect.bullet1.header') }}</h3>
              <p>{{ i18n.t('sellerJoinTodayDirect.bullet1.description') }}</p>
            </div>
          </div>
          <div class="flex text-ice-700 ll-mt-3">
            <Illustration class="ll-mr-2 flex-none" name="time" />
            <div>
              <h3>{{ i18n.t('sellerJoinTodayDirect.bullet2.header') }}</h3>
              <p>{{ i18n.t('sellerJoinTodayDirect.bullet2.description') }}</p>
            </div>
          </div>
          <div v-if="userStore.getIsSplitEnabled('fire_pod__direct_pay_seller_fee')" class="flex text-ice-700 ll-mt-3">
            <Illustration class="ll-mr-2 flex-none" name="retail" />
            <div>
              <h3>{{ i18n.t('sellerJoinTodayDirect.bullet3.header') }}</h3>
              <p>{{ i18n.t('sellerJoinTodayDirect.bullet3.description') }}</p>
            </div>
          </div>
          <div v-if="userStore.getIsSplitEnabled('fire_pod__cashback_rewards')" class="flex text-ice-700 ll-mt-3">
            <Illustration class="ll-mr-2 flex-none" name="money-transfer" />
            <div>
              <h3>{{ i18n.t('sellerJoinTodayDirect.bullet4WithCashback.header') }}</h3>
              <p>{{ i18n.t('sellerJoinTodayDirect.bullet4WithCashback.description') }}</p>
            </div>
          </div>
          <div v-else class="flex text-ice-700 ll-mt-3">
            <Illustration class="ll-mr-2 flex-none" name="money-transfer" />
            <div>
              <h3>{{ i18n.t('sellerJoinTodayDirect.bullet4.header') }}</h3>
              <p>{{ i18n.t('sellerJoinTodayDirect.bullet4.description') }}</p>
            </div>
          </div>
        </Box>

        <Box class="ll-pa-3 md:p-12 flex items-center justify-center ll-mt-4 lg:mt-0">
          <div>
            <h2 class="text-2xl ll-mb-5 text-center">{{ i18n.t('sellerJoinTodayDirect.signUpHeader') }}</h2>
            <IconLabel icon="check" color="blue-500" class="text-ice-900 md:text-base">
              {{ i18n.t('sellerJoinTodayDirect.signUpRequirements.first') }}
            </IconLabel>
            <IconLabel icon="check" color="blue-500" class="text-ice-900 md:text-base ll-mt-2">
              {{ i18n.t('sellerJoinTodayDirect.signUpRequirements.second') }}
            </IconLabel>
            <IconLabel icon="check" color="blue-500" class="text-ice-900 md:text-base ll-mt-2 ll-mb-5">
              {{ i18n.t('sellerJoinTodayDirect.signUpRequirements.third') }}
              <span class="text-ice-700">{{ i18n.t('common.optionalLabel') }}</span>
            </IconLabel>
            <Button class="font-weight-medium" :class="$style['hero-button']" @click="openTerms">
              {{ i18n.t('sellerJoinTodayDirect.ctaButton') }}
            </Button>
          </div>
        </Box>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  // Note: these global utilities should be removed once MP is on Stash 8+ and
  // this PR is merged: https://github.com/LeafLink/stash/pull/747
  :global(.md\:text-base) {
    @include breakpoint('md') {
      font-size: 1rem; /* 16px */
      line-height: 1.5rem; /* 24px */
    }
  }

  :global(.text-2xl) {
    font-size: 1.5rem; /* 24px */
    line-height: 2rem; /* 32px */
  }

  :global(.md\:p-12) {
    @include breakpoint('md') {
      padding: 3rem !important;
    }
  }

  :global(.justify-center) {
    justify-content: center;
  }

  :global(.lg\:grid) {
    @include breakpoint('lg') {
      display: grid;
    }
  }

  :global(.lg\:grid-cols-2) {
    @include breakpoint('lg') {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  :global(.lg\:gap-12) {
    @include breakpoint('lg') {
      gap: 3rem; /* 48px */
    }
  }

  // End Note

  .wrapper {
    min-height: 100%;
  }

  .header {
    background: url('/static/img/digital-onboarding/header-bg.svg') no-repeat center center;
    background-size: cover;

    @include breakpoint('lg') {
      min-height: 589px;
    }
  }

  .hero {
    padding-bottom: 4.25rem;
    padding-top: 4.375rem;
  }

  .hero-heading {
    font-size: 2.625rem;
    line-height: 2.875rem;

    @include breakpoint('md') {
      font-size: 3rem;
      line-height: 3.3125rem;
    }
  }

  .hero-description {
    font-size: 1rem;
    line-height: 1.5rem;

    @include breakpoint('md') {
      font-size: 1.25rem;
      line-height: 1.75rem;
    }
  }

  .hero-button {
    border-radius: 100px;
    display: block;
    font-size: 1.25rem;
    line-height: 3.4375rem;
    margin: 0 auto;
    max-width: 400px;
    width: 100%;
  }

  .payments-logo {
    height: 20px;
    opacity: 0.5;
  }

  // narrow container, as per design
  .container--narrow {
    max-width: 960px;
  }

  // narrow content container, as per design
  .content {
    margin-bottom: 2rem;
    margin-top: 2rem;
    max-width: 1154px;

    @include breakpoint('lg') {
      margin-bottom: 7.875rem;
      margin-top: -15.375rem; // 246px
    }
  }
</style>
